import { armPracticeCheckout } from './practiceCheckoutSession'
import { trackCheckoutStarted } from '@/lib/analytics/conversion'

export type CheckoutPaymentPlanType =
  | 'jd_prep'
  | 'skill_assessment'
  | 'system_design'
  | 'practice_question'
  | 'interview_combo'

/** User-facing product label on the checkout order row (by plan type, not catalog name). */
const CHECKOUT_DISPLAY_PRODUCT_NAMES: Record<CheckoutPaymentPlanType, string> = {
  jd_prep: 'Job Description Interview',
  skill_assessment: 'Mock Interview',
  system_design: 'System Design Interview',
  practice_question: 'Practice Question',
  interview_combo: 'Combo Plan',
}

export function getCheckoutDisplayProductName(
  paymentPlanType: CheckoutPaymentPlanType | string,
): string {
  if (paymentPlanType in CHECKOUT_DISPLAY_PRODUCT_NAMES) {
    return CHECKOUT_DISPLAY_PRODUCT_NAMES[
      paymentPlanType as CheckoutPaymentPlanType
    ]
  }
  return 'Product'
}

/** Cancel / abandon checkout for JD, mock, or system design interviews. */
export const CHECKOUT_INTERVIEW_CANCEL_PATH = '/dashboard'

export function isInterviewProductCheckout(
  paymentPlanType: CheckoutPaymentPlanType,
): paymentPlanType is Exclude<CheckoutPaymentPlanType, 'practice_question' | 'interview_combo'> {
  return (
    paymentPlanType === 'jd_prep' ||
    paymentPlanType === 'skill_assessment' ||
    paymentPlanType === 'system_design'
  )
}

/** Cancel and unpaid exit from checkout (interviews always return to dashboard). */
export function resolveCheckoutCancelDestination(
  paymentPlanType: CheckoutPaymentPlanType,
  returnTo: string,
): string {
  if (isInterviewProductCheckout(paymentPlanType)) return CHECKOUT_INTERVIEW_CANCEL_PATH
  if (paymentPlanType === 'interview_combo') return '/'
  return returnTo
}

export type BuildCheckoutUrlParams = {
  productId?: string
  paymentPlanType: CheckoutPaymentPlanType
  productPrice?: number
  questionId?: string
  returnTo?: string
}

export function buildCheckoutUrl({
  productId,
  paymentPlanType,
  productPrice,
  questionId,
  returnTo,
}: BuildCheckoutUrlParams): string {
  const params = new URLSearchParams({
    payment_plan_type: paymentPlanType,
  })
  if (productId) params.set('product_id', productId)
  if (productPrice != null) params.set('product_price', String(productPrice))
  if (questionId) params.set('question_id', questionId)
  if (returnTo) params.set('return_to', returnTo)
  return `/checkout?${params.toString()}`
}

/** Navigate to checkout for a catalog product (jd / mock / system design). */
export function navigateToProductCheckout(
  router: { push: (url: string) => void },
  params: {
    productId: string
    paymentPlanType: Exclude<CheckoutPaymentPlanType, 'practice_question'>
    productPrice?: number
    returnTo?: string
  },
): void {
  try {
    trackCheckoutStarted({ plan: params.paymentPlanType })
  } catch {
    // Analytics must never block checkout navigation.
  }
  router.push(buildCheckoutUrl(params))
}

/** Navigate to checkout for a single practice question purchase. */
export function navigateToPracticeQuestionCheckout(
  router: { push: (url: string) => void },
  params: {
    questionId: string
    productPrice: number
    returnTo?: string
  },
): void {
  armPracticeCheckout(params.questionId)
  try {
    trackCheckoutStarted({ plan: 'practice_question', source: 'practice_question' })
  } catch {
    // ignore
  }
  router.push(
    buildCheckoutUrl({
      paymentPlanType: 'practice_question',
      questionId: params.questionId,
      productPrice: params.productPrice,
      returnTo: params.returnTo,
    }),
  )
}
